import { Color, Icon, Text } from '@billy/design-system'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  hasLowercaseLetter,
  hasMinimumLength,
  hasNumber,
  hasSpecialCharacter,
  hasUppercaseLetter,
  MIN_PASSWORD_LENGTH,
} from '../../utils/passwordRequirements'
import * as Styled from './styles'

interface PasswordChecksProps {
  value: string
}

interface PasswordCheck {
  description: string
  isValid: (value: string) => boolean
}

export const PasswordChecks = ({ value }: PasswordChecksProps): ReactElement => {
  const { t } = useTranslation()
  const checks = useMemo<PasswordCheck[]>(
    () => [
      {
        description: t('password_check.min_length', { count: MIN_PASSWORD_LENGTH }),
        isValid: hasMinimumLength,
      },
      {
        description: t('password_check.lowercase_letter'),
        isValid: hasLowercaseLetter,
      },
      {
        description: t('password_check.uppercase_letter'),
        isValid: hasUppercaseLetter,
      },
      {
        description: t('password_check.number'),
        isValid: hasNumber,
      },
      {
        description: t('password_check.special_character'),
        isValid: hasSpecialCharacter,
      },
    ],
    [t],
  )

  return (
    <Styled.PasswordChecksList>
      {checks.map(({ description, isValid }) => {
        const isChecked = isValid(value)

        return (
          <Styled.PasswordChecksItem key={description}>
            <Styled.IconWrapper>
              <Icon color={isChecked ? Color.Green400 : Color.Grey500} icon={isChecked ? 'check' : 'x'} />
            </Styled.IconWrapper>
            <Text colorVariant="secondary">{description}</Text>
          </Styled.PasswordChecksItem>
        )
      })}
    </Styled.PasswordChecksList>
  )
}
